// ==========================================================================
//  gutachtenPdf.js — PDF-Auslesen fuer den Pflegegutachten-Pruefer (Etappe 4)
// ==========================================================================
//  Liest ein MD-/MEDICPROOF-Gutachten LOKAL im Browser aus (pdf.js) und
//  versucht, die Summenwerte der sechs NBA-Module zu erkennen.
//
//  Wichtig fuer den Datenschutz: Die PDF-Datei verlaesst den Rechner NICHT.
//  Es wird nichts an den Server hochgeladen — die Extraktion laeuft komplett
//  im Browser. Nur die erkannten Zahlen wandern (nach Bestaetigung durch den
//  Berater) in die Eingabemaske.
//
//  pdf.js wird per dynamischem Import geladen, damit die grosse Bibliothek
//  nur dann in den Browser kommt, wenn wirklich ein PDF eingelesen wird
//  (haelt den normalen Cockpit-Start schlank).
// ==========================================================================

// Maximale Summenwerte je Modul (identisch zu backend/services/nba_tabellen.py).
// Dienen hier als Plausibilitaets-Grenze bei der Zahlen-Erkennung.
export const MAX_SUMME = { 1: 15, 2: 33, 3: 65, 4: 54, 5: 15, 6: 18 };

// Stichworte, an denen sich die Modul-Zeile im Gutachten erkennen laesst.
// (Der Modul-Titel variiert leicht je nach Software — deshalb mehrere.)
const MODUL_STICHWORTE = {
  1: ['mobilit'],
  2: ['kognitive', 'kommunikativ'],
  3: ['verhaltensweisen', 'psychische problem'],
  4: ['selbstversorgung'],
  5: ['krankheits', 'therapiebedingt', 'bewältigung', 'bewaeltigung'],
  6: ['alltagsleben', 'soziale kontakte', 'gestaltung des alltags'],
};

let pdfjsPromise = null;

async function getPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const lib = await import('pdfjs-dist');
      // Worker als eigenes Bundle (Vite ?worker) — kein externer CDN-Aufruf.
      const WorkerMod = await import('pdfjs-dist/build/pdf.worker.min.mjs?worker');
      lib.GlobalWorkerOptions.workerPort = new WorkerMod.default();
      return lib;
    })();
  }
  return pdfjsPromise;
}

/**
 * Liest den kompletten Text-Layer eines PDF aus (alle Seiten).
 * Liefert { text, seiten, vermutlichScan }.
 * vermutlichScan=true, wenn (fast) kein Text gefunden wurde -> reiner Scan/
 * Foto, dort hilft nur manuelles Eintragen.
 */
export async function extrahiereText(file) {
  const lib = await getPdfjs();
  const daten = new Uint8Array(await file.arrayBuffer());
  const doc = await lib.getDocument({ data: daten }).promise;
  const teile = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const inhalt = await page.getTextContent();
    let seite = '';
    for (const item of inhalt.items) {
      if (typeof item.str !== 'string') continue;
      seite += item.str;
      seite += item.hasEOL ? '\n' : ' ';
    }
    teile.push(seite.trim());
  }
  const seiten = doc.numPages;
  await doc.destroy();
  const text = teile.join('\n\n').replace(/[ \t]+\n/g, '\n').trim();
  return {
    text,
    seiten,
    vermutlichScan: text.replace(/\s/g, '').length < 40,
  };
}

// Deutsche Zahl aus einem String ziehen (nur Ganzzahl; "12,0" -> 12).
function _ganzzahl(str) {
  if (!str) return null;
  const m = String(str).match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

/**
 * Sucht in einer Textzeile den Summenwert eines Moduls.
 * Bevorzugt eine Zahl direkt nach "Einzelpunkte"/"Summe der Punkte";
 * faellt sonst auf die erste plausible Ganzzahl (0..max) zurueck.
 */
function _wertAusZeile(zeile, max) {
  const l = zeile.toLowerCase();
  // 1. Bevorzugt: "... Einzelpunkte ... <zahl>" oder "Summe der Punkte: <zahl>"
  const nachStichwort = l.match(/(?:einzelpunkte|summe der (?:einzel)?punkte)[^\d]{0,15}(\d{1,2})/);
  if (nachStichwort) {
    const n = parseInt(nachStichwort[1], 10);
    if (n >= 0 && n <= max) return n;
  }
  // 2. Fallback: erste Ganzzahl 0..max in der Zeile
  const alle = (zeile.match(/\d{1,2}/g) || []).map((s) => parseInt(s, 10));
  for (const n of alle) {
    if (n >= 0 && n <= max) return n;
  }
  return null;
}

/**
 * Best-effort-Erkennung der sechs Modul-Summenwerte aus dem extrahierten
 * Gutachten-Text. Liefert je Modul { modul, wert|null, gefunden, kontext }.
 *
 * Bewusst konservativ: was nicht sicher erkennbar ist, bleibt null und muss
 * vom Berater im Kontroll-Schritt ergaenzt werden. Der Kontext (die gefundene
 * Zeile) wird mitgeliefert, damit die Zuordnung nachpruefbar ist.
 */
export function parseModulwerte(text) {
  const zeilen = (text || '').split('\n').map((z) => z.trim()).filter(Boolean);
  const ergebnis = [];

  for (let modul = 1; modul <= 6; modul++) {
    const max = MAX_SUMME[modul];
    const stichworte = MODUL_STICHWORTE[modul];
    let treffer = null;

    // Kandidaten-Zeilen: enthalten "Modul <n>" ODER ein Modul-Stichwort.
    const modulRegex = new RegExp(`modul\\s*${modul}\\b`, 'i');
    for (const zeile of zeilen) {
      const l = zeile.toLowerCase();
      const passt = modulRegex.test(zeile) || stichworte.some((s) => l.includes(s));
      if (!passt) continue;
      const wert = _wertAusZeile(zeile, max);
      if (wert !== null) {
        treffer = { wert, kontext: zeile };
        break;  // erste passende Zeile mit plausibler Zahl gewinnt
      }
      // Zeile passt thematisch, aber ohne Zahl -> als Kontext merken
      if (!treffer) treffer = { wert: null, kontext: zeile };
    }

    ergebnis.push({
      modul,
      wert: treffer ? treffer.wert : null,
      gefunden: !!(treffer && treffer.wert !== null),
      kontext: treffer ? treffer.kontext : null,
    });
  }
  return ergebnis;
}
